// ============================================================
//  src/pages/bots/PhoneRegistrationWizard.js
//
//  "Bring your own number" — walks a person through Meta's real
//  4-step phone number registration flow (add -> request code ->
//  verify code -> register with PIN), matching the actual Graph
//  API calls exactly. Reusable from two places:
//    1. Standalone, before any bot exists (Create Bot modal offers
//       a "Don't have a number yet?" link into this)
//    2. From an existing bot's Overview tab, registering a second
//       number on the same WABA — token/WABA ID pre-filled from
//       that bot's own settings, still editable.
//
//  Styled entirely with its own PhoneRegistrationWizard.css — does
//  NOT rely on whichever host page's CSS happens to be loaded, since
//  DashboardPage.css and BotDetailPage.css define shared class names
//  like .modal / .form-row-2 differently (or not at all), which is
//  exactly what made this look broken depending on where it opened.
// ============================================================
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import './PhoneRegistrationWizard.css';
import { addPhoneNumber, requestPhoneCode, verifyPhoneCode, completePhoneRegistration } from '../../api/client';

const COUNTRY_CODES = [
  ['234', 'Nigeria (+234)'], ['1', 'US / Canada (+1)'], ['44', 'UK (+44)'],
  ['91', 'India (+91)'], ['27', 'South Africa (+27)'], ['254', 'Kenya (+254)'],
  ['233', 'Ghana (+233)'], ['256', 'Uganda (+256)'], ['255', 'Tanzania (+255)'],
];

// Pulls the richest available error message out of a failed response —
// prefers the server's already-formatted message (which itself prefers
// Meta's error_user_msg + code + fbtrace_id over the vaguer top-level
// message field), falls back gracefully if the network call itself failed.
function extractErrorMessage(err, fallback) {
  return err?.response?.data?.message ?? fallback;
}

export default function PhoneRegistrationWizard({ initialToken = '', initialWabaId = '', onClose, onComplete }) {
  const [step, setStep]   = useState('details'); // details | code-choice | code-entry | pin | success
  const [busy, setBusy]   = useState(false);
  const [prId, setPrId]   = useState(null);

  const [form, setForm] = useState({
    token: initialToken,
    wabaId: initialWabaId,
    cc: '234',
    phoneNumber: '',
    displayName: '',
  });
  const [codeMethod, setCodeMethod] = useState('SMS');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [pin, setPin]         = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [result, setResult]   = useState(null);
  const [apiError, setApiError] = useState(null); // structured API error, shown as a persistent inline banner — not a transient toast, since these can be long and actionable (e.g. Meta error codes/trace IDs someone may need to read carefully or copy)

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (apiError) setApiError(null); };

  async function handleAddNumber(e) {
    e.preventDefault();
    setApiError(null);
    if (!form.token.trim())       return toast.error('WhatsApp token is required');
    if (!form.wabaId.trim())      return toast.error('WABA ID is required');
    if (!form.phoneNumber.trim()) return toast.error('Phone number is required');
    if (!form.displayName.trim()) return toast.error('Display name is required');

    setBusy(true);
    try {
      const res = await addPhoneNumber(form.token.trim(), form.wabaId.trim(), form.cc, form.phoneNumber.trim(), form.displayName.trim());
      if (res.data?.success) {
        setPrId(res.data.prId);
        setStep('code-choice');
      } else {
        setApiError(res.data?.message ?? 'Could not add this number');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleSendCode(method) {
    setCodeMethod(method);
    setApiError(null);
    setBusy(true);
    try {
      const res = await requestPhoneCode(prId, form.token.trim(), method);
      if (res.data?.success) {
        toast.success(`Code sent via ${method}`);
        setStep('code-entry');
      } else {
        setApiError(res.data?.message ?? 'Could not send the code');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  function handleDigitChange(i, val) {
    if (!/^\d?$/.test(val)) return; // one digit only
    const next = [...codeDigits];
    next[i] = val;
    setCodeDigits(next);
    if (val && i < 5) {
      document.getElementById(`prw-code-digit-${i + 1}`)?.focus();
    }
    if (val && i === 5 && next.every(d => d !== '')) {
      handleVerifyCode(next.join(''));
    }
  }

  async function handleVerifyCode(codeOverride) {
    const code = codeOverride ?? codeDigits.join('');
    if (code.length !== 6) return toast.error('Enter all 6 digits');
    setApiError(null);
    setBusy(true);
    try {
      const res = await verifyPhoneCode(prId, form.token.trim(), code);
      if (res.data?.success) {
        toast.success('Number verified');
        setStep('pin');
      } else {
        setApiError(res.data?.message ?? 'That code didn\'t work');
        setCodeDigits(['', '', '', '', '', '']);
        document.getElementById('prw-code-digit-0')?.focus();
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleComplete(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) return toast.error('PIN must be exactly 6 digits');
    if (pin !== pinConfirm)   return toast.error('PINs do not match');
    setApiError(null);

    setBusy(true);
    try {
      const res = await completePhoneRegistration(prId, form.token.trim(), pin, pinConfirm);
      if (res.data?.success) {
        setResult(res.data);
        setStep('success');
      } else {
        setApiError(res.data?.message ?? 'Registration failed');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  const STEP_ORDER = ['details', 'code-choice', 'code-entry', 'pin'];
  const stepIndex = STEP_ORDER.indexOf(step === 'code-choice' ? 'code-choice' : step);

  return (
    <div className="prw-backdrop" onClick={e => { if (e.target === e.currentTarget && step !== 'success') onClose(); }}>
      <div className="prw-modal">
        <div className="prw-header">
          <div className="prw-title">Register a Phone Number</div>
          <button className="prw-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {step !== 'success' && (
          <div className="prw-progress">
            {STEP_ORDER.map((s, i) => (
              <div key={s} className={`prw-progress-seg ${i <= stepIndex ? 'active' : ''}`} />
            ))}
          </div>
        )}

        {/* Pinned outside the scrollable body on purpose — an error here
            needs to be seen the instant it happens, not just when someone
            happens to be scrolled to the top of a long form. */}
        {apiError && (
          <div className="prw-error-banner-fixed">
            <div className="prw-banner warning prw-error-banner">
              <span className="prw-error-icon">⚠</span>
              <div className="prw-error-content">
                <div className="prw-error-text">{apiError}</div>
                <div className="prw-error-actions">
                  <button type="button" className="prw-link-btn" onClick={() => { navigator.clipboard?.writeText(apiError); toast.success('Copied'); }}>
                    Copy
                  </button>
                  <button type="button" className="prw-link-btn" onClick={() => setApiError(null)}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="prw-body">
          {step === 'details' && (
            <form onSubmit={handleAddNumber} id="prw-details-form">
              <div className="prw-field">
                <label className="prw-label">WhatsApp Access Token</label>
                <input className="prw-input" placeholder="EAAxxxx…" value={form.token}
                  onChange={e => set('token', e.target.value)} />
                <div className="prw-hint">From Meta Business → WhatsApp → API Setup</div>
              </div>
              <div className="prw-field">
                <label className="prw-label">WABA ID</label>
                <input className="prw-input" placeholder="Your WhatsApp Business Account ID" value={form.wabaId}
                  onChange={e => set('wabaId', e.target.value)} />
              </div>
              <div className="prw-row-2 prw-field">
                <div>
                  <label className="prw-label">Country</label>
                  <select className="prw-select" value={form.cc} onChange={e => set('cc', e.target.value)}>
                    {COUNTRY_CODES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="prw-label">Phone Number</label>
                  <input className="prw-input" placeholder="8012345678" value={form.phoneNumber}
                    onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, ''))} />
                  <div className="prw-hint">Without the country code or leading 0</div>
                </div>
              </div>
              <div className="prw-field" style={{ marginBottom: 0 }}>
                <label className="prw-label">Display Name</label>
                <input className="prw-input" placeholder="What customers will see, e.g. PocketFi" value={form.displayName}
                  onChange={e => set('displayName', e.target.value)} />
              </div>
            </form>
          )}

          {step === 'code-choice' && (
            <>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
                How should Meta send you the verification code for <strong>+{form.cc}{form.phoneNumber}</strong>?
              </p>
              <div className="prw-choice-row">
                <button type="button" className="prw-choice-card" disabled={busy} onClick={() => handleSendCode('SMS')}>
                  <span className="icon">📱</span>Text me a code
                </button>
                <button type="button" className="prw-choice-card" disabled={busy} onClick={() => handleSendCode('VOICE')}>
                  <span className="icon">📞</span>Call me with a code
                </button>
              </div>
            </>
          )}

          {step === 'code-entry' && (
            <>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
                Enter the 6-digit code sent via {codeMethod === 'SMS' ? 'text message' : 'phone call'} to{' '}
                <strong>+{form.cc}{form.phoneNumber}</strong>.
              </p>
              <div className="prw-otp-row">
                {codeDigits.map((d, i) => (
                  <input
                    key={i}
                    id={`prw-code-digit-${i}`}
                    className="prw-otp-digit"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    disabled={busy}
                    onChange={e => handleDigitChange(i, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Backspace' && !d && i > 0) document.getElementById(`prw-code-digit-${i - 1}`)?.focus(); }}
                  />
                ))}
              </div>
              <div className="prw-otp-actions">
                <button type="button" className="prw-link-btn" disabled={busy} onClick={() => handleSendCode(codeMethod)}>
                  Resend code
                </button>
                <button type="button" className="prw-btn primary" disabled={busy} style={{ flex: '0 0 auto' }}
                  onClick={() => handleVerifyCode()}>
                  {busy && <span className="prw-spinner" />}{busy ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </>
          )}

          {step === 'pin' && (
            <form onSubmit={handleComplete} id="prw-pin-form">
              <div className="prw-banner info">
                💡 This PIN protects your number's two-step verification — Meta may ask for it again later. We'll
                store it securely (encrypted) so you don't have to remember it yourself, but it's worth writing down
                too in case you ever need it outside this platform.
              </div>
              <div className="prw-row-2">
                <div>
                  <label className="prw-label">6-digit PIN</label>
                  <input className="prw-input" inputMode="numeric" maxLength={6} placeholder="••••••"
                    value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} />
                </div>
                <div>
                  <label className="prw-label">Confirm PIN</label>
                  <input className="prw-input" inputMode="numeric" maxLength={6} placeholder="••••••"
                    value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, ''))} />
                </div>
              </div>
            </form>
          )}

          {step === 'success' && result && (
            <>
              <div className="prw-success-icon">
                <div className="emoji">✅</div>
                <div className="prw-success-title">Number registered and ready</div>
              </div>

              <div className="prw-detail-row"><span className="prw-detail-key">Number</span><span className="prw-detail-val">{result.phoneNumber}</span></div>
              <div className="prw-detail-row"><span className="prw-detail-key">Display Name</span><span className="prw-detail-val">{result.displayName}</span></div>

              <div className="prw-banner info" style={{ marginTop: 16 }}>
                💾 Save these two values somewhere safe — they're exactly what you'll need to create a bot with this
                number, here or later.
              </div>

              <div className="prw-copy-field">
                <label className="prw-label">WhatsApp Access Token</label>
                <div className="prw-copy-row">
                  <span className="prw-copy-value">{form.token.trim()}</span>
                  <button type="button" className="prw-copy-btn"
                    onClick={() => { navigator.clipboard?.writeText(form.token.trim()); toast.success('Token copied'); }}>
                    Copy
                  </button>
                </div>
              </div>

              <div className="prw-copy-field">
                <label className="prw-label">Phone Number ID</label>
                <div className="prw-copy-row">
                  <span className="prw-copy-value">{result.phoneNumberId}</span>
                  <button type="button" className="prw-copy-btn"
                    onClick={() => { navigator.clipboard?.writeText(result.phoneNumberId); toast.success('Phone Number ID copied'); }}>
                    Copy
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer — buttons live outside prw-body so they never scroll away on a short screen */}
        {step === 'details' && (
          <div className="prw-footer">
            <button type="button" className="prw-btn outline" onClick={onClose}>Cancel</button>
            <button type="submit" form="prw-details-form" className="prw-btn primary" disabled={busy}>
              {busy && <span className="prw-spinner" />}{busy ? 'Adding…' : 'Add Number'}
            </button>
          </div>
        )}
        {step === 'pin' && (
          <div className="prw-footer">
            <button type="submit" form="prw-pin-form" className="prw-btn primary full" disabled={busy}>
              {busy && <span className="prw-spinner" />}{busy ? 'Completing…' : 'Complete Registration'}
            </button>
          </div>
        )}
        {step === 'success' && (
          <div className="prw-footer">
            <button className="prw-btn primary full"
              onClick={() => onComplete({ phoneNumberId: result.phoneNumberId, token: form.token.trim(), wabaId: form.wabaId.trim(), displayName: result.displayName })}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}